import { z } from 'zod';

/**
 * IANA timezone handling — the ONE place that decides whether a `users.timezone`
 * string is usable, shared by the write edge (`PATCH /me`), the domain
 * (`ProfileService`, proactive arming) and the background workers (proactive
 * fire, streak tick).
 *
 * Why it is shared rather than three local guards: every calendar computation in
 * the product (quiet hours, the user-tz daily counters, streak ordinals) hands
 * the stored string to `Intl`, and `Intl` THROWS a `RangeError` on a zone it
 * cannot resolve. A single unvalidated write therefore wedges a retrying BullMQ
 * worker forever — it throws before `next_fire_at` can advance, so every retry
 * re-reads the same poison row. Two rules close that:
 *
 *  1. **Reject at the write edge** ({@link TimeZone}) — garbage never lands in
 *     the column in the first place.
 *  2. **Fall back on read** ({@link resolveTimeZone}) — a row written before the
 *     validation existed (or by a script/import that bypassed it) degrades to
 *     {@link DEFAULT_TIME_ZONE} instead of poisoning the loop. Wrong-by-an-offset
 *     is recoverable; wedged-forever is not.
 */

/**
 * The zone used whenever a user has none recorded (browser detection pending) or
 * the recorded one is unusable. UTC keeps every calendar computation total.
 */
export const DEFAULT_TIME_ZONE = 'UTC';

/**
 * Longest timezone string accepted. The longest real IANA name is well under 40
 * characters; the cap exists so an unbounded body can't reach `Intl`.
 */
export const MAX_TIME_ZONE_LENGTH = 64;

/**
 * Bound on the memoised valid-zone set. The VALID input set is not small: `Intl`
 * is case-insensitive, so `Europe/Berlin` alone has 2^11 accepted spellings (and
 * a three-segment zone name far more), plus ~5,700 fixed-offset spellings
 * (`+05:30`, `+0530`, `+05`, …). Since the probe input is user-supplied, the
 * memo is BOUNDED — and cleared rather than frozen when it fills: a
 * first-come-first-served cap that stops accepting entries lets junk spellings
 * lock out the real hot set permanently, turning every subsequent probe into a
 * ~90µs synchronous ICU construction on the event loop. Clearing costs one cold
 * pass and the hot set re-populates.
 */
const MAX_MEMOISED_ZONES = 1_024;

const memoised = new Set<string>();

/**
 * Can `Intl` resolve this string as a timezone? Probes the engine (rather than
 * matching a hardcoded list) so link names (`US/Pacific`), canonical names and
 * offset zones (`+05:30`) are all accepted, and the answer always matches what
 * the formatter downstream will actually do.
 *
 * TOTAL for any input — including a non-string that reached it through a
 * structurally-typed port — because its callers are error paths that must not
 * themselves throw.
 */
export function isValidTimeZone(value: string): boolean {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_TIME_ZONE_LENGTH) {
    return false;
  }
  if (memoised.has(value)) {
    return true;
  }
  if (!probe(value)) {
    return false;
  }
  if (memoised.size >= MAX_MEMOISED_ZONES) {
    memoised.clear();
  }
  memoised.add(value);
  return true;
}

/**
 * The zone to actually compute in: the stored value when it is usable, otherwise
 * `fallback` (default {@link DEFAULT_TIME_ZONE}). Total — never throws — so a
 * caller on a hot retry loop cannot be wedged by one bad row.
 */
export function resolveTimeZone(
  value: string | null | undefined,
  fallback: string = DEFAULT_TIME_ZONE,
): string {
  return typeof value === 'string' && isValidTimeZone(value) ? value : fallback;
}

/**
 * The wire contract for a settable timezone (`PATCH /me`). Length-bounded FIRST
 * so the `Intl` probe only ever sees a short string.
 */
export const TimeZone = z
  .string()
  .min(1)
  .max(MAX_TIME_ZONE_LENGTH)
  .refine(isValidTimeZone, { message: 'expected a resolvable IANA timezone (e.g. Europe/Berlin)' });
export type TimeZone = z.infer<typeof TimeZone>;

/** Ask the engine; a zone it cannot resolve throws `RangeError`, which is the answer. */
function probe(value: string): boolean {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: value }).resolvedOptions().timeZone !== '';
  } catch {
    return false;
  }
}
